/**
 * @file   src\helpers\message.js
 * @brief  This file is responsible for defining common functions in messages section
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import { db } from "../firebase";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  query,
  collection,
  addDoc,
  serverTimestamp,
  where,
  setDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

import { getUserId, loggedInUser, getLocalStorage } from "./common";
import { USER_INFO_KEY } from "../constants/common";
import { isImpersonatedToken } from "./impersonationHelper";

const userDetails = loggedInUser();
const FIREBASE_PASSWORD = process.env.REACT_APP_FIREBASE_USER_PASSWORD;

// These writes reach Firestore directly from the browser - they never go
// through the API, so the backend's impersonation denylist
// (blockImpersonatedSideEffects) can't see or block them. Without this,
// an admin impersonating a customer could send/receive real chat messages
// as that customer, delivered to the counterparty in real time.
const isImpersonating = () =>
  isImpersonatedToken(getLocalStorage(USER_INFO_KEY)?.token?.accessToken);

export async function loginOrSignUp(email) {
  const auth = getAuth();

  await signInWithEmailAndPassword(auth, email, FIREBASE_PASSWORD)
    .then((userCredential) => {
      // Signed in
      const user = userCredential.user;
      return 1;
      // ...
    })
    .catch(async (error) => {
      await createUserWithEmailAndPassword(auth, email, FIREBASE_PASSWORD)
        .then((userCredential) => {
          // Signed in
          const user = userCredential.user;
          return 1;
          // ...
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
        });
    });
}

export const signOutFirebase = () => {
  const auth = getAuth();
  signOut(auth)
    .then(() => {
      return 1;
    })
    .catch((error) => {
      return 0;
    });
};

export const addMessage = async (introId, data) => {
  if (isImpersonating()) return 0;

  const auth = getAuth();
  var user = auth.currentUser;
  if (!user) {
    let email = _.get(userDetails, "userInfo.email", null);
    await loginOrSignUp(email);
  }

  try {
    const docRef = collection(db, "Chat", introId.toString(), "message");
    let response = await addDoc(docRef, {
      ...data,
      createdAt: serverTimestamp(),
    });

    return 1;
  } catch (e) {
    return 0;
  }
};
export const addDirectMessage = async (data) => {
  if (isImpersonating()) return 0;

  const auth = getAuth();
  var user = auth.currentUser;
  console.log({ data });
  if (!user) {
    let email = _.get(userDetails, "userInfo.email", null);
    await loginOrSignUp(email);
  }

  try {
    // const chatId =
    //   data.senderId < data.receiverId
    //     ? `${data.senderId}_${data.receiverId}`
    //     : `${data.receiverId}_${data.senderId}`;
    const chatRef = doc(db, "DirectChat", data.chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      console.log("Creating new DirectChat...");
      await setDoc(chatRef, {
        users: [data.senderId, data.receiverId], // Store users
        createdAt: serverTimestamp(), // Chat creation timestamp
        lastMessage: null, // No last message initially
        lastMessageAt: null, // No last message timestamp initially
        unreadBy: [], // Track which users have unread messages
      });
    }

    const messagesRef = collection(db, "DirectChat", data.chatId, "messages");

    let response = await addDoc(messagesRef, {
      ...data,
      createdAt: serverTimestamp(),
    });

    await updateDoc(chatRef, {
      lastMessage: data.text || "MEET", // Store the last message
      lastMessageAt: serverTimestamp(), // Timestamp of last message
      unreadBy: arrayUnion(data.receiverId), // Mark receiver as having unread
    });

    return 1;
  } catch (e) {
    console.error("Error adding message:", e);
    return 0;
  }
};

export const addIntroduction = async (introId, data) => {
  const auth = getAuth();
  var user = auth.currentUser;
  if (!user) {
    let email = _.get(userDetails, "userInfo.email", null);
    await loginOrSignUp(email);
  }

  // Create an initial document to update.
  // const docRef = doc(db, "Introduction", introId.toString(), "message");
  // let response = await addDoc(docRef, { ...data, createdAt: serverTimestamp() });

  try {
    let response = await setDoc(doc(db, "Introduction", introId.toString()), {
      ...data,
      createdat: serverTimestamp(),
    });
    return 1;
  } catch (e) {
    return 0;
  }
};

export const updateIntroduction = async (introId, data) => {
  const auth = getAuth();
  var user = auth.currentUser;
  if (!user) {
    let email = _.get(userDetails, "userInfo.email", null);
    await loginOrSignUp(email);
  }

  // Create an initial document to update.
  // const docRef =  collection(db, "Introduction", introId.toString(), "message");
  try {
    let response = await updateDoc(
      doc(db, "Introduction", introId.toString()),
      { ...data, createdat: serverTimestamp() },
    );

    return 1;
  } catch (e) {
    return 0;
  }
};

export const updateIntroductionWithOutCreatedDate = async (introId, data) => {
  // Create an initial document to update.
  // const docRef =  collection(db, "Introduction", introId.toString(), "message");
  try {
    let response = await updateDoc(
      doc(db, "Introduction", introId.toString()),
      { ...data },
    );
    return 1;
  } catch (e) {
    return 0;
  }
};

export const deleteIntroduction = async (introId) => {
  const docRef = doc(db, "Introduction", introId.toString());
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    try {
      let response = await deleteDoc(
        doc(db, "Introduction", introId.toString()),
      );
      return 1;
    } catch (e) {
      return 0;
    }
  } else {
    return 1;
  }
};

export const markDirectChatAsRead = async (chatId, userId) => {
  try {
    await updateDoc(doc(db, "DirectChat", chatId), {
      unreadBy: arrayRemove(userId),
    });
    return 1;
  } catch (e) {
    return 0;
  }
};

export const deleteMessageDoc = async (introId) => {
  let userId = getUserId();

  const q = query(
    collection(db, "Chat", introId.toString(), "message"),
    where("isTyping", "==", true),
    where("createdUserId", "==", userId),
  );

  const querySnapshot = await getDocs(q);
  let id = [];
  querySnapshot.forEach((doc) => {
    // doc.data() is never undefined for query doc snapshots
    id = doc.id;
  });

  const docRef = doc(db, "Chat", introId.toString(), "message", id.toString());
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    try {
      let response = await deleteDoc(docRef);
      return 1;
    } catch (e) {
      return 0;
    }
  } else {
    return 1;
  }
};
